/* eslint-disable react/prop-types */
import React, { useMemo } from 'react';
import { Pagination, PaginationItem, PaginationLink, Row, Col, Input } from 'reactstrap';

export interface TablePaginationProps {
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions?: number[];
  isLoading?: boolean;
}

export const TablePagination = React.memo(
  React.forwardRef<HTMLDivElement, TablePaginationProps>((props, ref) => {
    const {
      pageNumber,
      pageSize,
      totalCount,
      totalPages,
      hasNextPage,
      hasPreviousPage,
      onPageChange,
      onPageSizeChange,
      pageSizeOptions = [10, 20, 50, 100],
      isLoading = false,
    } = props;

    const startRecord = useMemo(() => {
      return totalCount === 0 ? 0 : (pageNumber - 1) * pageSize + 1;
    }, [pageNumber, pageSize, totalCount]);

    const endRecord = useMemo(() => {
      return Math.min(pageNumber * pageSize, totalCount);
    }, [pageNumber, pageSize, totalCount]);

    const paginationItems = useMemo(() => {
      const pages: number[] = [];
      const maxPagesToShow = 5;

      if (totalPages <= maxPagesToShow) {
        // Show all pages if total pages is less than or equal to max
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Show first page, last page, and pages around current page
        pages.push(1);

        if (pageNumber > 3) {
          pages.push(-1); // Ellipsis
        }

        const startPage = Math.max(2, pageNumber - 1);
        const endPage = Math.min(totalPages - 1, pageNumber + 1);

        for (let i = startPage; i <= endPage; i++) {
          pages.push(i);
        }

        if (pageNumber < totalPages - 2) {
          pages.push(-1); // Ellipsis
        }

        pages.push(totalPages);
      }

      return pages;
    }, [totalPages, pageNumber]);

    return (
      <div ref={ref} className="table-pagination mt-4">
        <Row className="align-items-center">
          {/* Info Section */}
          <Col md="4" className="mb-3 mb-md-0">
            <small className="text-muted">
              Showing {startRecord} to {endRecord} of {totalCount.toLocaleString()} items
            </small>
          </Col>

          {/* Page Size Selector */}
          <Col md="3" className="mb-3 mb-md-0">
            <div className="d-flex align-items-center gap-2">
              <label htmlFor="page-size" className="small text-muted mb-0">
                Items per page:
              </label>
              <Input
                id="page-size"
                type="select"
                value={pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                disabled={isLoading}
                className="form-select-sm"
                style={{ width: 'auto' }}
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </Input>
            </div>
          </Col>

          {/* Pagination Controls */}
          <Col md="5" className="d-flex justify-content-end">
            <Pagination size="sm" className="mb-0">
              {/* Previous Button */}
              <PaginationItem disabled={!hasPreviousPage || isLoading}>
                <PaginationLink
                  onClick={() => onPageChange(pageNumber - 1)}
                  disabled={!hasPreviousPage || isLoading}
                  className="rounded-start"
                >
                  <i className="ri-arrow-left-s-line"></i>
                  Previous
                </PaginationLink>
              </PaginationItem>

              {/* Page Numbers */}
              {paginationItems.map((page, index) => {
                if (page === -1) {
                  return (
                    <PaginationItem key={`ellipsis-${index}`} disabled>
                      <PaginationLink>...</PaginationLink>
                    </PaginationItem>
                  );
                }

                return (
                  <PaginationItem key={page} active={pageNumber === page}>
                    <PaginationLink
                      onClick={() => onPageChange(page)}
                      disabled={isLoading}
                      className={pageNumber === page ? 'active' : ''}
                    >
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}

              {/* Next Button */}
              <PaginationItem disabled={!hasNextPage || isLoading}>
                <PaginationLink
                  onClick={() => onPageChange(pageNumber + 1)}
                  disabled={!hasNextPage || isLoading}
                  className="rounded-end"
                >
                  Next
                  <i className="ri-arrow-right-s-line"></i>
                </PaginationLink>
              </PaginationItem>
            </Pagination>
          </Col>
        </Row>

        {/* Page Info */}
        <Row className="mt-2">
          <Col>
            <small className="text-muted">
              Page {pageNumber} of {totalPages}
            </small>
          </Col>
        </Row>
      </div>
    );
  }),
);

TablePagination.displayName = 'TablePagination';
